import {
  ActivitySquare,
  AlertCircle,
  Bell,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  CreditCard,
  GitBranch,
  Globe,
  HardDrive,
  LayoutDashboard,
  Layers,
  LogOut,
  Pause,
  PhoneCall,
  Settings,
  Shield,
  Sparkles,
  UserCheck,
  Users,
  UsersRound,
  Webhook,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { logoutRequest } from "../api/auth.js";
import { useAuthStore } from "../store/authStore.js";
import { canAccess } from "../lib/planGate.js";
import { usePermission } from "../lib/usePermission.js";
import ProBadge from "./ProBadge.jsx";
import icon from "../assets/icon-outline.svg";

// True when a gated nav item should render with the PRO badge for the active
// plan. Gated items are always visible — never hidden — so users can discover
// Professional features; clicking through lands on the masked upgrade screen.
function isLocked(plan, gate) {
  return !!gate && !canAccess(gate, plan);
}

// True when `pathname` is `to` or a sub-route of it ("/teams/1" matches
// "/teams") -- NOT a bare string-prefix check. "/teams".startsWith("/team")
// is true, which previously made visiting the Operations "Teams" page
// (/teams) also light up the unrelated Settings "Team" item (/team, the
// workspace members page) and auto-expand its group.
function pathMatches(pathname, to) {
  return pathname === to || pathname.startsWith(`${to}/`);
}

// Whether a nav item should appear at all. Unlike plan gating (which shows a
// locked badge on root), these hide the item entirely:
//   item.perm        — required permission the user must hold
//   item.requireRoot — only on a root workspace (e.g. billing)
// Plan-locked items are also hidden in child workspaces — children can't
// upgrade, so a locked teaser is pointless; only root sees the PRO badge.
function itemVisible(item, { can, isRoot, plan }) {
  if (item.perm && !can(item.perm)) return false;
  if (item.requireRoot && !isRoot) return false;
  if (!isRoot && isLocked(plan, item.gate)) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Nav structure
// ---------------------------------------------------------------------------

const TOP_LEVEL_ITEMS = [
  { to: "/dashboard", labelKey: "sidebar.dashboard", Icon: LayoutDashboard },
  { to: "/wizard", labelKey: "sidebar.wizard", Icon: Sparkles },
  { to: "/incidents", labelKey: "sidebar.incidents", Icon: AlertCircle },
  {
    to: "/status-pages",
    labelKey: "sidebar.status_pages",
    Icon: Globe,
    gate: "status_pages",
  },
];

const NAV_GROUPS = [
  {
    key: "monitoring",
    labelKey: "sidebar.monitoring",
    Icon: ActivitySquare,
    items: [
      { to: "/sites", labelKey: "sidebar.sites", Icon: Globe },
      { to: "/monitors", labelKey: "sidebar.monitors", Icon: ActivitySquare },
      { to: "/groups", labelKey: "sidebar.groups", Icon: Layers, gate: "groups" },
      { to: "/rum", labelKey: "sidebar.rum", Icon: Globe, gate: "rum" },
      { to: "/servers", labelKey: "sidebar.servers", Icon: HardDrive, gate: "servers" },
      { to: "/maintenance-windows", labelKey: "sidebar.maintenance", Icon: Pause },
    ],
  },
  {
    key: "alerting",
    labelKey: "sidebar.alerting",
    Icon: Bell,
    items: [
      { to: "/channels", labelKey: "sidebar.channels", Icon: UserCheck },
      { to: "/on-call", labelKey: "sidebar.on_call", Icon: PhoneCall, gate: "on_call" },
      { to: "/inbound-webhooks", labelKey: "sidebar.inbound_webhooks", Icon: Webhook },
    ],
  },
  {
    key: "operations",
    labelKey: "sidebar.operations",
    Icon: UsersRound,
    items: [
      {
        to: "/teams",
        labelKey: "sidebar.teams",
        Icon: UsersRound,
        perm: "teams.view",
        gate: "operations",
      },
      {
        to: "/services",
        labelKey: "sidebar.services_ops",
        Icon: Layers,
        perm: "services.view",
        gate: "operations",
      },
      {
        to: "/routing-rules",
        labelKey: "sidebar.routing_rules",
        Icon: GitBranch,
        perm: "routing_rules.view",
        gate: "operations",
      },
    ],
  },
  {
    key: "settings",
    labelKey: "sidebar.settings",
    Icon: Settings,
    items: [
      { to: "/settings", labelKey: "sidebar.general", Icon: Settings },
      { to: "/team", labelKey: "sidebar.team", Icon: Users },
      {
        to: "/workspaces",
        labelKey: "sidebar.workspaces",
        Icon: Building2,
        requireRoot: true,
      },
      {
        to: "/billing",
        labelKey: "sidebar.billing",
        Icon: CreditCard,
        perm: "billing.view",
        requireRoot: true,
      },
      { to: "/audit-log", labelKey: "sidebar.audit_log", Icon: ClipboardList },
    ],
  },
];

const COLLAPSED_KEY = "sidebar-collapsed";
const GROUPS_KEY = "sidebar-groups-open";

// ---------------------------------------------------------------------------
// NavItem
// ---------------------------------------------------------------------------

function NavItem({ to, label, Icon, collapsed, mobileOpen, onClick, locked = false }) {
  const showLabel = !collapsed || mobileOpen;
  return (
    <NavLink
      to={to}
      onClick={onClick}
      title={collapsed && !mobileOpen ? label : undefined}
      className={({ isActive }) =>
        [
          "flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors",
          isActive
            ? "bg-teal-600 text-white"
            : "text-gray-400 hover:bg-gray-800 hover:text-white",
        ].join(" ")
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {showLabel && <span className="flex-1">{label}</span>}
      {showLabel && locked && <ProBadge className="h-4 w-auto shrink-0" />}
    </NavLink>
  );
}

// ---------------------------------------------------------------------------
// NavGroup — expandable section
// ---------------------------------------------------------------------------

function NavGroup({
  group,
  collapsed,
  mobileOpen,
  openGroups,
  onToggle,
  onNavClick,
  plan,
  can,
  isRoot,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const visibleItems = group.items.filter((item) =>
    itemVisible(item, { can, isRoot, plan })
  );
  if (visibleItems.length === 0) return null;
  const isAnyChildActive = visibleItems.some((item) =>
    pathMatches(location.pathname, item.to)
  );

  // When collapsed (icon-only), just render items as individual icons — no group header
  if (collapsed && !mobileOpen) {
    return (
      <div className="space-y-0.5">
        {visibleItems.map(({ to, labelKey, Icon, gate }) => (
          <NavItem
            key={to}
            to={to}
            label={t(labelKey)}
            Icon={Icon}
            collapsed={collapsed}
            mobileOpen={mobileOpen}
            onClick={onNavClick}
            locked={isLocked(plan, gate)}
          />
        ))}
      </div>
    );
  }

  const isOpen = openGroups.has(group.key) || isAnyChildActive;

  return (
    <div>
      {/* Group header button */}
      <button
        type="button"
        onClick={() => onToggle(group.key)}
        className={[
          "flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors",
          isAnyChildActive
            ? "text-teal-400"
            : "text-gray-500 hover:bg-gray-800 hover:text-gray-300",
        ].join(" ")}
      >
        <group.Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">{t(group.labelKey)}</span>
        <ChevronDown
          className={[
            "h-3.5 w-3.5 shrink-0 transition-transform duration-200",
            isOpen ? "rotate-180" : "",
          ].join(" ")}
        />
      </button>

      {/* Children */}
      {isOpen && (
        <div className="ml-3 mt-0.5 border-l border-gray-700 pl-2 space-y-0.5">
          {visibleItems.map(({ to, labelKey, Icon, gate }) => (
            <NavItem
              key={to}
              to={to}
              label={t(labelKey)}
              Icon={Icon}
              collapsed={false}
              mobileOpen={mobileOpen}
              onClick={onNavClick}
              locked={isLocked(plan, gate)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workspace switcher modal
// ---------------------------------------------------------------------------

function OrgSwitcherModal({ workspaces, currentOrgId, onClose, onSwitch }) {
  const { t } = useTranslation();
  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-sm rounded-xl bg-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">
            {t("sidebar.switch_workspace")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
        <ul className="max-h-80 overflow-y-auto py-1">
          {workspaces.map((workspace) => {
            const isCurrent = String(workspace.id) === String(currentOrgId);
            return (
              <li key={workspace.id}>
                <button
                  type="button"
                  disabled={isCurrent}
                  onClick={() => onSwitch(workspace.id)}
                  className={[
                    "flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors",
                    isCurrent
                      ? "bg-teal-50 text-teal-700 cursor-default"
                      : "text-gray-700 hover:bg-gray-50",
                  ].join(" ")}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-xs font-bold uppercase text-gray-600">
                    {workspace.name.slice(0, 2)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{workspace.name}</p>
                    <p className="truncate text-xs text-gray-400">{workspace.role}</p>
                  </div>
                  {isCurrent && (
                    <span className="shrink-0 rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700">
                      {t("sidebar.active")}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

export default function Sidebar({ mobileOpen = false, onMobileClose }) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_KEY) === "true"
  );
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = localStorage.getItem(GROUPS_KEY);
      const parsed = saved ? JSON.parse(saved) : ["monitoring"];
      // Accordion: keep at most one group open.
      return new Set(parsed.slice(0, 1));
    } catch {
      return new Set(["monitoring"]);
    }
  });
  const [showSwitcher, setShowSwitcher] = useState(false);

  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const workspace = useAuthStore((s) => s.workspace);
  const plan = workspace?.plan ?? "standard";
  const can = usePermission();
  const isRoot = workspace?.parent_id == null;
  const workspaces = useAuthStore((s) => s.workspaces);
  const logout = useAuthStore((s) => s.logout);
  const setWorkspace = useAuthStore((s) => s.setWorkspace);

  function toggle() {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_KEY, String(next));
      return next;
    });
  }

  function toggleGroup(key) {
    setOpenGroups((prev) => {
      const next = prev.has(key) ? new Set() : new Set([key]);
      localStorage.setItem(GROUPS_KEY, JSON.stringify([...next]));
      return next;
    });
  }

  async function handleLogout() {
    try {
      await logoutRequest();
    } catch {
      // best-effort
    }
    logout();
    navigate("/login", { replace: true });
  }

  function handleNavClick() {
    if (onMobileClose) onMobileClose();
  }

  function handleSwitch(workspaceId) {
    if (!workspaceId || String(workspaceId) === String(workspace?.id)) {
      setShowSwitcher(false);
      return;
    }
    const target = workspaces.find((o) => String(o.id) === String(workspaceId));
    if (!target) return;
    setWorkspace(target);
    setShowSwitcher(false);
    navigate("/dashboard", { replace: true });
  }

  const showExpanded = !collapsed || mobileOpen;

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={onMobileClose}
        />
      )}

      <aside
        className={[
          "flex flex-col bg-gray-900 text-gray-100 transition-all duration-200",
          "md:relative md:translate-x-0",
          collapsed ? "md:w-16" : "md:w-56",
          "fixed inset-y-0 left-0 z-40 w-64",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          "md:flex",
        ].join(" ")}
      >
        {/* Logo + collapse toggle */}
        <div className="flex h-14 items-center justify-between px-3 border-b border-gray-700">
          <img src={icon} height="28" width="28" className="m-1" />
          {showExpanded && (
            <span className="flex text-sm font-heading tracking-wide font-bold text-teal-600 truncate">
              Monitero
            </span>
          )}
          <button
            type="button"
            onClick={toggle}
            className="ml-auto rounded p-1.5 text-gray-400 hover:bg-gray-800 hover:text-white hidden md:block"
            aria-label={collapsed ? t("common.expand") : t("common.collapse")}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
          <button
            type="button"
            onClick={onMobileClose}
            className="ml-auto rounded p-1.5 text-gray-400 hover:bg-gray-800 hover:text-white md:hidden"
            aria-label={t("common.close_menu")}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>

        {/* Workspace switcher */}
        {workspace && (
          <div className="border-b border-gray-700 px-3 py-2.5">
            {!showExpanded ? (
              <button
                type="button"
                title={workspace.name}
                onClick={() => workspaces.length > 1 && setShowSwitcher(true)}
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-800 text-xs font-bold uppercase text-gray-300 hover:bg-gray-700 mx-auto"
              >
                {workspace.name.slice(0, 2)}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => workspaces.length > 1 && setShowSwitcher(true)}
                disabled={workspaces.length <= 1}
                className={[
                  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors",
                  workspaces.length > 1
                    ? "hover:bg-gray-800 cursor-pointer"
                    : "cursor-default",
                ].join(" ")}
              >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-700 text-xs font-bold uppercase text-gray-200">
                  {workspace.name.slice(0, 2)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-gray-100">
                    {workspace.name}
                  </p>
                  <p className="truncate text-[10px] text-gray-500 capitalize">
                    {workspace.plan}
                  </p>
                </div>
                {workspaces.length > 1 && (
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-500" />
                )}
              </button>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto no-scrollbar py-3 px-2 space-y-1">
          {/* Top-level items */}
          <div className="space-y-0.5">
            {TOP_LEVEL_ITEMS.filter((item) => itemVisible(item, { can, isRoot, plan })).map(
              ({ to, labelKey, Icon, gate }) => (
                <NavItem
                  key={to}
                  to={to}
                  label={t(labelKey)}
                  Icon={Icon}
                  collapsed={collapsed}
                  mobileOpen={mobileOpen}
                  onClick={handleNavClick}
                  locked={isLocked(plan, gate)}
                />
              )
            )}
          </div>

          {/* Divider */}
          <div className="my-2 border-t border-gray-700/60" />

          {/* Grouped sections */}
          <div className="space-y-1">
            {NAV_GROUPS.map((group) => (
              <NavGroup
                key={group.key}
                group={group}
                collapsed={collapsed}
                mobileOpen={mobileOpen}
                openGroups={openGroups}
                onToggle={toggleGroup}
                onNavClick={handleNavClick}
                plan={plan}
                can={can}
                isRoot={isRoot}
              />
            ))}
          </div>
        </nav>

        {/* User block */}
        <div className="border-t border-gray-700 px-2 py-3 space-y-1">
          <NavLink
            to="/user"
            onClick={handleNavClick}
            title={!showExpanded ? t("sidebar.user_profile") : undefined}
            className={({ isActive }) =>
              [
                "flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-teal-600 text-white"
                  : "text-gray-400 hover:bg-gray-800 hover:text-white",
              ].join(" ")
            }
          >
            <Shield className="h-4 w-4 shrink-0" />
            {showExpanded && (
              <div className="min-w-0 text-left">
                <p className="truncate">
                  {user?.full_name || user?.email || t("sidebar.user_profile")}
                </p>
                <p className="truncate text-xs">{t("sidebar.account_security")}</p>
              </div>
            )}
          </NavLink>
          <button
            type="button"
            onClick={handleLogout}
            title={!showExpanded ? t("sidebar.logout") : undefined}
            className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {showExpanded && <span>{t("sidebar.logout")}</span>}
          </button>
        </div>
      </aside>

      {showSwitcher && (
        <OrgSwitcherModal
          workspaces={workspaces}
          currentOrgId={workspace?.id}
          onClose={() => setShowSwitcher(false)}
          onSwitch={handleSwitch}
        />
      )}
    </>
  );
}
